import './root.css';

import React, { useState, useEffect, useMemo, Suspense, useCallback } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Topbar from '../components/topbar/topbar';
import useScrollStore from '../stores/scrollStore';
import throttle from 'lodash/throttle';
import useAlertStore from '../stores/alertStore';
import useCreateRequestModalStore from '../stores/createRequestModalStore';
import { loginPathFor } from '../utils/loginRedirect';
import useTicketSystemEnabled from '../hooks/useTicketSystemEnabled';
import useInvestorDetailStore from '../stores/investorDetailStore';
import useViewportHeight from '../hooks/useViewportHeight';
import { checkSession, logout } from '../services/authService';
import { HttpError } from "../utils/http-error";
import { logoutNotificationService } from '../services/logoutNotificationService';
import useConstructionUpdateStore from '../stores/constructionUpdateStore';
import { useTranslation } from 'react-i18next';
import ScrollUpButton from '../components/scroll-up-button/scroll-up-button';
import useUnitDetailStore from '../stores/unitDetailStore';
import useDashboardStore, { DASHBOARD_STORAGE_NAME } from '../stores/dashboardStore';
import useFinancialStore from '../stores/financialStore';
import useUnitStore, { UNIT_STORAGE_NAME } from '../stores/unitStore';
import NavigationBottomBar from '../components/bottom-navigation-bar/bottom-navigation-bar';
import useIsMobile from '../hooks/useIsMobile';
import GlobalFileViewer from '../components/file-viewer/global-file-viewer';
import Alert from '../components/alert/alert';
import { ContentAreaProvider } from '../context/ContentAreaContext';
import * as Sentry from "@sentry/react";
import { Capacitor } from '@capacitor/core';
import { Style } from '@capacitor/status-bar';
import { StatusBar } from '@capacitor/status-bar';
import useTenantDetailStore from '../stores/tenantDetailStore';
import useLanguageStore from '../stores/languageStore';
import AppUrlListener from './app-url-listener';
import tenantBg from "../assets/images/tenant-background.webp";
import tenantMobileBg from "../assets/images/tenant-mobile-background.webp";
import investorBg from "../assets/images/investor-background.webp";
import investorBgDark from "../assets/images/investor-background-dark.webp";
import investorLpMobileBg from "../assets/images/landing-page/investor-lp-mobile-background.webp";
import investorLpBg from "../assets/images/landing-page/investor-lp-background.webp";
import { initAppReview } from '../utils/appReview';
import Sidebar from '../components/sidebar/sidebar';
import OldTopbar from '../components/old-topbar/old-topbar';
import useCustomNavigate from '../hooks/useCustomNavigate';
import useMobileBackGesture from '../hooks/useMobileBackGesture';
import useSmoothScroll from '../hooks/useSmoothScroll';
import MobileLoginBg from '../assets/images/mobile-login-bg.webp';

import BitPalmAgent from '../components/bitpalm-agent/bitpalm-agent';
import CookieBanner from '../components/cookie-banner/cookie-banner';

const ContactUsModal = React.lazy(() => import('../modals/contact-us-modal/contact-us-modal'));
const CreateRequestModal = React.lazy(() => import('../modals/create-request-modal/create-request-modal'));

const Root = () => {
    useViewportHeight();

    // Initialize mobile back gesture/button handling
    useMobileBackGesture();

    const location = useLocation();
    const isMobile = useIsMobile();
    const { t, i18n } = useTranslation();
    const navigate = useCustomNavigate();
    const ticketsEnabled = useTicketSystemEnabled();

    // References and state for content area
    const [contentAreaElement, setContentAreaElement] = useState<HTMLDivElement | null>(null);

    // Combined ref that handles both ref assignment and state update
    const contentAreaRef = useCallback((node: HTMLDivElement | null) => {
        setContentAreaElement(node);
    }, []);

    // Smooth scrolling for landing pages
    useSmoothScroll(contentAreaElement);

    // Stores
    const { investor, reset: resetInvestoreDetailStore } = useInvestorDetailStore();
    const { tenant, reset: resetTenantDetailStore } = useTenantDetailStore();
    const setAtBottom = useScrollStore(state => state.setAtBottom);
    const hideAlert = useAlertStore(state => state.hideAlert);
    const isCreateRequestVisible = useCreateRequestModalStore(state => state.isVisible);
    const resetUnitStore = useUnitStore(state => state.reset);
    const resetUnitDetailStore = useUnitDetailStore(state => state.reset);
    const resetConstructionUpdateStore = useConstructionUpdateStore(state => state.reset);
    const resetDashboardStore = useDashboardStore(state => state.reset);
    const resetFinancialStore = useFinancialStore(state => state.reset);

    // States
    const [lastVisible, setLastVisible] = useState<number>(Date.now());
    const [isSidebarVisible, setSidebarVisible] = useState(false);

    // Functions
    const scrollToTop = useCallback(() => {
        const contentArea = contentAreaElement;
        if (!contentArea) return;

        // Verwendung eines kleinen Timeouts, um sicherzustellen, dass der Scroll nach Seitenwechseln funktioniert
        setTimeout(() => {
            if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
                // For iOS native, use direct jump
                contentArea.scrollTop = 0;

                // Zusätzliche Sicherheit: Nach 50ms nochmal scrollen, falls der erste Versuch fehlschlägt
                setTimeout(() => {
                    if (contentArea.scrollTop > 0) {
                        contentArea.scrollTop = 0;
                    }
                }, 50);
            } else {
                // For other platforms, use smooth scrolling
                contentArea.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                });
            }
        }, 50);
    }, [contentAreaElement]);

    const runCheckAuth = useCallback(async () => {
        if (!tenant && !investor) return;

        try {
            await checkSession();
        } catch (error) {
            /**
             * Only sign out when the session is actually gone.
             *
             * This used to treat every failure the same, which turned a rate
             * limit or a dropped connection into a full sign-out: stores wiped,
             * refresh token thrown away, back to the login screen. On the app
             * that is the worst version of it, because the refresh token there
             * is good for seven days and the person has to type their password
             * again to get a new one. 401 and 403 are the only answers that
             * mean the session is over; anything else is worth another try
             * later.
             */
            const abgelaufen = error instanceof HttpError && error.isAuthFailure;
            if (!abgelaufen) {
                console.log('Session check failed, keeping the session: ', error);
                return;
            }

            console.log('Error while checking user session: ', error);
            await logout();

            resetUnitStore();
            resetConstructionUpdateStore();
            resetInvestoreDetailStore();
            resetTenantDetailStore();
            resetUnitDetailStore();
            resetDashboardStore();
            resetFinancialStore();

            // Read from window rather than the router hook, which would make
            // this check run again on every navigation. Someone opening a mail
            // link days later still has the old sign-in stored, so the page
            // renders first and only this check sends them to the login.
            navigate(loginPathFor(window.location), { replace: true, clearHistory: true });
        }
    }, [tenant, investor, navigate, resetUnitStore, resetConstructionUpdateStore, resetInvestoreDetailStore, resetTenantDetailStore, resetUnitDetailStore, resetDashboardStore, resetFinancialStore]);

    // Language store
    const { language: storedLanguage, deviceLanguageDetected, setLanguage, setDeviceLanguageDetected } = useLanguageStore();

    // Global language restoration - restore stored language for all routes
    useEffect(() => {
        if (storedLanguage && storedLanguage !== i18n.language) {
            console.log(`Restoring stored language: ${storedLanguage}`);
            i18n.changeLanguage(storedLanguage);
        }
    }, [storedLanguage]); // Only depend on storedLanguage to avoid loops

    // Language detection and storage for landing pages only
    useEffect(() => {
        const isLandingPage = ['/home', '/tenant-home', '/details'].includes(location.pathname);

        if (!isLandingPage) return;

        // Only run language detection/setup once per page load
        const setupLanguage = () => {
            console.log('Language setup for landing page:', {
                currentLang: i18n.language,
                storedLanguage,
                deviceLanguageDetected
            });

            if (!deviceLanguageDetected && !storedLanguage) {
                // First visit: detect device language
                const deviceLanguage = navigator.language || navigator.languages?.[0] || 'de';
                const detectedLang = deviceLanguage.startsWith('de') ? 'de' : 'en';

                console.log(`First visit - detecting device language: ${deviceLanguage} -> Using: ${detectedLang}`);

                // Set the detected language
                i18n.changeLanguage(detectedLang);

                // Mark that we've detected device language
                setDeviceLanguageDetected(true);
            }
        };

        // Only setup language on initial load
        setupLanguage();
    }, [location.pathname, storedLanguage, deviceLanguageDetected, setDeviceLanguageDetected]); // Removed setLanguage to avoid conflicts

    // Global language change listener to store user preference
    useEffect(() => {
        const handleLanguageChange = (lng: string) => {
            setLanguage(lng);
        };

        i18n.on('languageChanged', handleLanguageChange);

        return () => {
            i18n.off('languageChanged', handleLanguageChange);
        };
    }, [setLanguage]); // Only depend on setLanguage

    // Dynamic page title and meta description based on locale
    useEffect(() => {
        document.title = t('meta.page_title');
        document.documentElement.lang = i18n.language;

        const metaDescription = document.querySelector('meta[name="description"]');
        if (metaDescription) {
            metaDescription.setAttribute('content', t('meta.page_description'));
        }

        const ogTitle = document.querySelector('meta[property="og:title"]');
        if (ogTitle) {
            ogTitle.setAttribute('content', t('meta.page_title'));
        }

        const ogDescription = document.querySelector('meta[property="og:description"]');
        if (ogDescription) {
            ogDescription.setAttribute('content', t('meta.page_description'));
        }

        const twitterTitle = document.querySelector('meta[name="twitter:title"]');
        if (twitterTitle) {
            twitterTitle.setAttribute('content', t('meta.page_title'));
        }

        const twitterDescription = document.querySelector('meta[name="twitter:description"]');
        if (twitterDescription) {
            twitterDescription.setAttribute('content', t('meta.page_description'));
        }
    }, [i18n.language, t]);

    // App review
    useEffect(() => {
        if (!Capacitor.isNativePlatform()) return;
        initAppReview({
            iosAppId: '6745959836',
            minSessions: 3,
            triggerInterval: 5,
            maxPrompts: 3,
            cooldownDays: 90,
        });
    }, []);

    // Initial authentication check on app startup
    /*useEffect(() => {
        const checkInitialAuth = async () => {
            const { setIsLoading: setInvestorLoading, setIsInitalLoaded: setInvestorLoaded } = useInvestorDetailStore.getState();
            const { setIsLoading: setTenantLoading, setIsInitalLoaded: setTenantLoaded } = useTenantDetailStore.getState();

            try {
                // Check if user is authenticated
                await checkSession();

                // If we reach here, user is authenticated - try to fetch user data
                // First check if we already have user data in stores (from persistence)
                if (!investor && !tenant) {
                    try {
                        // Try to fetch investor data first
                        const { fetchInvestor } = await import('../services/investor/investorService');
                        const investorData = await fetchInvestor();
                        setInvestor(investorData);
                        setInvestorLoaded(true);
                    } catch (error) {
                        // If investor fetch fails, try tenant
                        try {
                            const { fetchTenant } = await import('../services/tenant/tenantService');
                            const tenantData = await fetchTenant();
                            setTenant(tenantData);
                            setTenantLoaded(true);
                        } catch (tenantError) {
                            console.log('User is authenticated but no valid user data found');
                        }
                    }
                } else {
                    // If we already have user data from persistence, mark as loaded
                    if (investor) {
                        setInvestorLoaded(true);
                    }
                    if (tenant) {
                        setTenantLoaded(true);
                    }
                }
            } catch (error) {
                // User is not authenticated - that's fine
                console.log('User not authenticated on startup');
            } finally {
                // Always set loading to false when we're done
                setInvestorLoading(false);
                setTenantLoading(false);
            }
        };

        checkInitialAuth();
    }, []); // Run only once on component mount*/

    // Set sentry user
    useEffect(() => {
        if (!investor && !tenant) {
            Sentry.setUser(null);
            return;
        }

        if (investor) {
            Sentry.setUser({
                type: "investor",
                id: investor.investor_id,
                email: investor.email,
                username: `${investor.first_name} ${investor.last_name}`
            });
        } else if (tenant) {
            Sentry.setUser({
                type: "tenant",
                id: tenant.tenant_id,
                email: tenant.email,
                username: `${tenant.first_name} ${tenant.last_name}`
            });
        }
    }, [investor, tenant]);

    // Reset scrolling state
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const categoryId = searchParams.get('categoryId');

        if (location.pathname !== "/faq" || (location.pathname === "/faq" && !categoryId)) {
            // Priorität: Scrolling sofort starten
            scrollToTop();

            // Zusätzliche Sicherheit: Nach 100ms nochmal scrollen, falls der erste Versuch fehlschlägt
            const timer = setTimeout(() => {
                const contentArea = contentAreaElement;
                if (contentArea && contentArea.scrollTop > 0) {
                    scrollToTop();
                }
            }, 100);

            return () => clearTimeout(timer);
        }
    }, [location, scrollToTop, contentAreaElement]);

    // Clear old unused local storage
    useEffect(() => {
        localStorage.removeItem(DASHBOARD_STORAGE_NAME);
        localStorage.removeItem(UNIT_STORAGE_NAME);
    }, []);

    // Scroll handler to track reach bottom
    useEffect(() => {
        const scrollContainer = contentAreaElement;
        if (scrollContainer) {
            const handleScroll = throttle(() => {
                const scrollPosition = scrollContainer.scrollTop + scrollContainer.clientHeight;
                const offsetHeight = scrollContainer.scrollHeight;
                const reachedBottom = scrollPosition >= offsetHeight - 80;
                setAtBottom(reachedBottom);
            }, 300);

            scrollContainer.addEventListener('scroll', handleScroll);

            return () => {
                scrollContainer.removeEventListener('scroll', handleScroll);
            };
        }
    }, [setAtBottom, contentAreaElement]);

    // Hide alert after navigation
    useEffect(() => {
        hideAlert();
        if (location.pathname === "/home" || location.pathname === "/tenant-home" || location.pathname === "/login" || location.pathname === "/forgot-password") {
            setSidebarVisible(false);
        }
    }, [location]);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                setLastVisible(Date.now());
            } else if (document.visibilityState === 'visible') {
                const now = Date.now();
                const diff = now - lastVisible;

                if (diff > 1800000) { // 30 min
                    runCheckAuth();
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [lastVisible]);

    useEffect(() => {
        runCheckAuth();
    }, [runCheckAuth]);

    // Cross-tab logout detection
    useEffect(() => {
        const unsubscribe = logoutNotificationService.onLogout(async () => {
            console.log('Logout detected in another tab');

            // Reset all stores
            resetUnitStore();
            resetConstructionUpdateStore();
            resetInvestoreDetailStore();
            resetTenantDetailStore();
            resetUnitDetailStore();
            resetDashboardStore();
            resetFinancialStore();

            // Navigate to login
            navigate("/login", { replace: true, clearHistory: true });
        });

        // Cleanup on unmount
        return () => {
            unsubscribe();
        };
    }, [navigate, resetUnitStore, resetConstructionUpdateStore, resetInvestoreDetailStore, resetTenantDetailStore, resetUnitDetailStore, resetDashboardStore, resetFinancialStore]);

    /*useEffect(() => {
        if (contentAreaElement) {
            const isMobileView = () => window.innerWidth < 992;

            if (isMobileView()) {
                contentAreaElement.style.overflowY = isSidebarVisible ? 'hidden' : 'auto';
            }

            return () => {
                if (isMobileView()) {
                    document.body.style.overflow = 'auto';
                }
            };
        }
    }, [isSidebarVisible, contentAreaElement]);*/

    const toggleSidebar = () => {
        setSidebarVisible(!isSidebarVisible);
    };

    const isFullPage = useMemo(() => {
        return location.pathname === "/" || location.pathname === "/download" || location.pathname === "/home" || location.pathname === "/tenant-home" || location.pathname === "/details" ||
            (location.pathname.includes("/articles") && !location.pathname.includes("/knowledge-hub")) || location.pathname === "/login" || location.pathname === "/reset-password" ||
            location.pathname === "/packages" || location.pathname === "/privacy-policy" ||
            location.pathname === "/terms-and-conditions" || location.pathname === "/onboarding" || location.pathname === "/handover" || location.pathname === "/tenant-registration" || location.pathname === "/book-valuation" ||
            location.pathname.startsWith("/oauth/")
    }, [location]);

    const theme: "dark" | "light" = useMemo(() => {
        if (investor && location.pathname === "/dashboard") return "dark";
        return "light";
    }, [investor, location]);

    // Background and StatusBar effects
    useEffect(() => {
        const backgroundColor = theme === "dark" ? "#040F2B" : "#D2DFF7";

        // Handle landing pages with body background
        if (location.pathname === "/home" || location.pathname === "/details") {
            document.body.style.backgroundColor = "white";
            document.documentElement.style.backgroundColor = "white";
            document.body.style.backgroundImage = `url(${isMobile ? investorLpMobileBg : investorLpBg})`;
            document.body.style.backgroundSize = "contain";
            document.body.style.backgroundPosition = "top";
            document.body.style.backgroundRepeat = "no-repeat";
        } else if (location.pathname === "/tenant-home") {
            document.body.style.backgroundColor = "white";
            document.documentElement.style.backgroundColor = "white";
            document.body.style.backgroundImage = `url(${isMobile ? tenantMobileBg : tenantBg})`;
            document.body.style.backgroundSize = "contain";
            document.body.style.backgroundPosition = "top";
            document.body.style.backgroundRepeat = "no-repeat";
        }

        // Handle StatusBar changes on native platforms
        if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
            StatusBar.setOverlaysWebView({ overlay: true }).catch(() => { });
            StatusBar.setStyle({ style: theme === "dark" ? Style.Dark : Style.Light }).catch(() => { });
            StatusBar.setBackgroundColor({ color: backgroundColor }).catch(() => { });
        }
    }, [theme, isMobile, location]);

    // Content area background effect - uses state to track element availability
    useEffect(() => {
        if (!contentAreaElement) return;

        // Helper function to reset content area styles
        const resetContentAreaStyles = () => {
            contentAreaElement.style.backgroundImage = "none";
            contentAreaElement.style.backgroundRepeat = "none";
            contentAreaElement.style.backgroundPosition = "none";
            contentAreaElement.style.backgroundSize = "none";
            contentAreaElement.style.backgroundAttachment = "none";
            contentAreaElement.style.backgroundColor = "white";
            contentAreaElement.style.background = "none";
        };

        const resetBodyStyles = () => {
            document.body.style.backgroundColor = "white";
            document.documentElement.style.backgroundColor = "white";
            document.body.style.backgroundImage = "none";
            document.body.style.backgroundSize = "none";
            document.body.style.backgroundPosition = "none";
            document.body.style.backgroundRepeat = "none";
        };

        // Skip if we're on landing pages (handled by first useEffect)
        if (location.pathname === "/home" || location.pathname === "/details" || location.pathname === "/tenant-home") return;

        if (location.pathname === "/handover" || location.pathname === "/onboarding" || location.pathname === "/reset-password" || location.pathname === "/book-valuation" || location.pathname.startsWith("/oauth/")) {
            resetBodyStyles();
            resetContentAreaStyles();
            return;
        }

        if (location.pathname.includes("/login")) {
            // Reset content area styles first
            resetContentAreaStyles();

            if (isMobile) {
                document.body.style.backgroundColor = "white";
                document.body.style.backgroundImage = `url(${MobileLoginBg})`;
                document.body.style.backgroundSize = "cover";
                document.body.style.backgroundAttachment = "local";
            } else {
                resetBodyStyles();
            }
            return;
        }

        if (!isMobile) {
            resetBodyStyles();
            resetContentAreaStyles();

            // Apply content area background based on user type
            const backgroundImage = tenant && !location.pathname.includes("/onboarding")
                ? `url(${tenantBg})`
                : investor && location.pathname.includes("/dashboard") ? `url(${investorBgDark})` : `url(${investorBg})`;

            // Apply all background styles
            Object.assign(contentAreaElement.style, {
                backgroundImage,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "top center",
                backgroundSize: "contain",
                backgroundAttachment: "local",
                backgroundColor: "white"
            });
        } else {
            resetBodyStyles();
            resetContentAreaStyles();

            const backgroundColor = theme === "dark" ? "#040F2B" : "#D2DFF7";
            document.body.style.backgroundColor = backgroundColor;

            contentAreaElement.style.background = tenant && !location.pathname.includes("/onboarding") ? "linear-gradient(178.95deg, #D2DFF7 7.78%, #CCDFFF 27.79%, #FFFFFF 50.36%)" :
                investor && location.pathname.includes("/dashboard") ? "linear-gradient(193.1deg, #040F2B 5.07%, #263C5E 19.65%, #1C52B5 23.42%, rgba(255, 255, 255, 1) 36.26%)" : "linear-gradient(178.95deg, #D2DFF7 7.78%, #CCDFFF 27.79%, #FFFFFF 50.36%)";

            if (investor && location.pathname.includes("/dashboard")) {
                contentAreaElement.style.backgroundAttachment = "local";
            } else {
                contentAreaElement.style.backgroundAttachment = "none";
            }
        }
    }, [investor, contentAreaElement, tenant, isMobile, location, theme]);

    return (
        <ContentAreaProvider contentAreaRef={{ current: contentAreaElement }}>
            <AppUrlListener />
            {/* Early in the DOM so the keyboard reaches it first; it floats anyway. */}
            <CookieBanner />
            <Suspense fallback={<div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>}>
                <div>
                    <Alert />
                    {isFullPage ? (
                        <>
                            {location.pathname !== "/download" && location.pathname !== "/home" && location.pathname !== "/tenant-home" && location.pathname !== "/details" && !(location.pathname.includes("/articles") && !location.pathname.includes("/knowledge-hub")) && location.pathname !== "/login" && location.pathname !== "/handover" && location.pathname !== "/book-valuation" && location.pathname !== "/tenant-registration" && location.pathname !== "/reset-password" && location.pathname !== "/privacy-policy" && location.pathname !== "/terms-and-conditions" && !location.pathname.startsWith("/oauth/") && (
                                <Topbar toggleSidebar={toggleSidebar} theme={theme} deactivateSidebar />
                            )}
                            {location.pathname === "/reset-password" && (
                                <OldTopbar toggleSidebar={toggleSidebar} isFullPage deactivateSidebar />
                            )}
                            <div id={location.pathname === "/download" || location.pathname === "/home" || location.pathname === "/tenant-home" || location.pathname === "/details" || (location.pathname.includes("/articles") && !location.pathname.includes("/knowledge-hub")) || location.pathname === "/login" || location.pathname.includes("/tenant-registration") || location.pathname === "/privacy-policy" || location.pathname === "/terms-and-conditions" || location.pathname.startsWith("/oauth/") ? "content-area-full-page" : "content-area"} ref={contentAreaRef}>
                                <Outlet />
                            </div>
                        </>
                    ) : (
                        <>
                            <div>
                                <Sidebar isSidebarVisible={isSidebarVisible} toggleSidebar={toggleSidebar} />
                                {isMobile && (
                                    <Topbar toggleSidebar={toggleSidebar} theme={theme} />
                                )}
                                <div id="content-area" ref={contentAreaRef}>
                                    {!isMobile && (
                                        <Topbar toggleSidebar={toggleSidebar} theme={theme} />
                                    )}
                                    <Outlet />
                                    <ScrollUpButton scrollableRef={{ current: contentAreaElement }} />
                                    <NavigationBottomBar />
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </Suspense>

            <GlobalFileViewer />

            <Suspense fallback={null}>
                <ContactUsModal />
                {ticketsEnabled && isCreateRequestVisible && <CreateRequestModal />}
            </Suspense>

            <BitPalmAgent />
        </ContentAreaProvider>
    );
};

export default Root;
